import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://night4night.com';
const DEFAULT_IMAGE = `${SITE_URL}/web-app-manifest-512x512.png`;

type SeoRoute =
  | 'home'
  | 'swaps'
  | 'mySwaps'
  | 'exchange'
  | 'guide'
  | 'faq'
  | 'about'
  | 'insurance'
  | 'contact'
  | 'terms'
  | 'privacy'
  | 'imprint'
  | 'notFound';

const PUBLIC_ROUTES: Record<string, SeoRoute> = {
  '/': 'home',
  '/swaps': 'swaps',
  '/guide': 'guide',
  '/faq': 'faq',
  '/about-us': 'about',
  '/insurance-info': 'insurance',
  '/contact': 'contact',
  '/agb': 'terms',
  '/datenschutz': 'privacy',
  '/impressum': 'imprint',
};

// Signed-in routes stay noindex but still need a real document title instead of
// falling through to the "page not found" copy.
const PRIVATE_ROUTES: Record<string, SeoRoute> = {
  '/my-swaps': 'mySwaps',
};

const NOINDEX_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/confirm-password-reset',
  '/my-',
  '/chats',
  '/confirm-exchange',
  '/checkout-success',
  '/rate-exchange',
  '/exchange/new',
];

function setMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value));
}

function setLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }
  element.href = href;
}

function setAlternate(hreflang: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(
    `link[rel="alternate"][hreflang="${hreflang}"]`
  );
  if (!element) {
    element = document.createElement('link');
    element.rel = 'alternate';
    element.hreflang = hreflang;
    document.head.appendChild(element);
  }
  element.href = href;
}

export function SeoManager() {
  const location = useLocation();
  const { t, i18n } = useTranslation(['seo', 'landing']);

  useEffect(() => {
    const isExchange = /^\/exchange\/[^/]+$/.test(location.pathname);
    const noindex = NOINDEX_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
    const route = isExchange
      ? 'exchange'
      : PUBLIC_ROUTES[location.pathname] || PRIVATE_ROUTES[location.pathname] || 'notFound';
    const title = t(`seo:${route}.title`);
    const description = t(`seo:${route}.description`);
    // Unknown routes must not canonicalize to the arbitrary 404 pathname.
    const canonicalPath =
      route === 'swaps' ? '/swaps' : route === 'notFound' ? '/' : location.pathname;
    const canonical = `${SITE_URL}${canonicalPath}`;
    const language = i18n.language.startsWith('en') ? 'en' : 'de';
    const robots = noindex || route === 'notFound' ? 'noindex, nofollow' : 'index, follow';

    document.title = title;
    document.documentElement.lang = language;
    setLink('canonical', canonical);
    // EN and DE variants live on the same URL (client-side language switch),
    // so all hreflang values point at the canonical URL.
    ['de', 'en', 'x-default'].forEach((hreflang) => setAlternate(hreflang, canonical));
    setMeta('meta[name="description"]', { name: 'description', content: description });
    setMeta('meta[name="robots"]', { name: 'robots', content: robots });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    setMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    });
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_IMAGE });
    setMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: language === 'de' ? 'de_DE' : 'en_US',
    });
    setMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    setMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description,
    });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_IMAGE });

    const schemas: object[] = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Night4Night',
        url: SITE_URL,
        logo: DEFAULT_IMAGE,
        description: t('seo:organization.description'),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Night4Night',
        url: SITE_URL,
        inLanguage: ['de', 'en'],
      },
    ];

    if (route === 'faq') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: ['how-safe-is-it', 'what-if-something-breaks', 'how-much-does-it-cost'].map(
          (key) => ({
            '@type': 'Question',
            name: t(`landing:faq.${key}.question`),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t(`landing:faq.${key}.answer`).replace(/<[^>]+>/g, ' '),
            },
          })
        ),
      });
    }

    let script = document.head.querySelector<HTMLScriptElement>('script[data-night4night-seo]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.night4nightSeo = 'true';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schemas);
  }, [i18n.language, location.pathname, t]);

  return null;
}
